import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { User, Mail, Phone, Lock, Target } from 'lucide-react';
import { getApiUrl, saveToken } from '@/lib/utils';

const API_URL = getApiUrl();

const CRICKET_ROLES = [
  'Batsman',
  'Bowler',
  'Keeper',
  'Batting Allrounder',
  'Bowling Allrounder'
];

function Register() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    cricket_role: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!formData.cricket_role) {
      toast.error('Please select your cricket role');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, formData, {
        withCredentials: true
      });
      // Save token to multiple storage locations for PWA persistence
      await saveToken(response.data.session_token);
      toast.success('Welcome to Weakenders!');
      navigate('/dashboard', { state: { user: response.data.user } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img 
            src="/images/weakenders-logo.png"
            alt="Weakenders Logo"
            className="w-24 h-24 mx-auto mb-4"
            data-testid="register-logo"
          />
          <h1 className="text-3xl font-bold text-[#0F172A]">Join The Team</h1>
          <p className="text-slate-600 mt-2">Create your Weakenders account</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          <form onSubmit={handleRegister} className="space-y-5" data-testid="register-form">
            <div>
              <Label htmlFor="name" className="text-sm font-semibold text-slate-700">Full Name</Label>
              <div className="relative mt-2">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="name"
                  type="text"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="pl-10 rounded-lg"
                  required
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-sm font-semibold text-slate-700">Email</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 rounded-lg"
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="phone" className="text-sm font-semibold text-slate-700">Phone Number</Label>
              <div className="relative mt-2">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+91 XXXXX XXXXX"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="pl-10 rounded-lg"
                  required
                  data-testid="register-phone-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="cricket_role" className="text-sm font-semibold text-slate-700">Cricket Role</Label>
              <div className="relative mt-2">
                <Target className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 z-10" />
                <Select
                  value={formData.cricket_role}
                  onValueChange={(value) => setFormData({ ...formData, cricket_role: value })}
                >
                  <SelectTrigger className="pl-10 rounded-lg" data-testid="register-role-select">
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent>
                    {CRICKET_ROLES.map((role) => (
                      <SelectItem key={role} value={role} data-testid={`role-${role.toLowerCase().replace(/\s/g, '-')}`}>
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-semibold text-slate-700">Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 rounded-lg"
                  required
                  minLength={6}
                  data-testid="register-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#E63946] hover:bg-[#DC2626] text-white font-bold uppercase tracking-wider py-6 rounded-lg shadow-sm"
              data-testid="register-submit-btn"
            >
              {loading ? 'Creating Account...' : 'Register'}
            </Button>
          </form>

          <p className="mt-6 text-center text-slate-600">
            Already have an account?{' '}
            <Link to="/login" className="text-[#E63946] font-semibold hover:underline" data-testid="login-link">
              Login here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Register;